import React, { PureComponent } from 'react';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import { defineMessages, WrappedComponentProps, injectIntl } from 'react-intl';
import {
  patientToFormValue,
  formValueToPatient,
  PatientDataFormValue,
  formDateFormats,
  RaceCodes,
  allRaceCodes,
} from './formValueMapping';
import { maskedModeSelectors } from '../../redux/modules/maskedMode';
import { connect } from 'react-redux';
import { MaskedModeState } from '../../redux/modules/maskedMode/reducer';
import { RootState } from '../../redux/rootReducer';
import clsx from 'clsx';
import { Formik, Form, Field } from 'formik';
import { TextFieldProps } from '@lifeomic/chroma-react/components/TextField';
import getValidators, {
  Validators,
  ValidationSignature,
  combineValidators,
} from '../../util/getValidators';
import { FormikTextField, FormikSelect } from '../ChromaFormik';
import { Button } from '@lifeomic/chroma-react/components/Button';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import PhoneNumberField from './PhoneNumberField';
import { getTestProps } from '../../util/testProps';
import { SelectOption } from '@lifeomic/chroma-react/components/Select';
import DateField from './DateField';
import { FormBox } from '@lifeomic/chroma-react/components/FormBox';
import { Text } from '@lifeomic/chroma-react/components/Text';
import PatientCohortAssignmentAlert from './PatientCohortAssignmentAlert';
import SocialSecurityNumberField from './SocialSecurityNumberField';
import { FormatMessage } from '../../util/intl';
import { SupportedIntlPhoneNumberValidation } from '../../common/types/Projects';
import { getCountriesWithCodes } from '../../util/countryUtils';

export const testIds = {
  rootFormElement: 'patientDataCollection-rootElement',
  maskedModeDisplay: 'patientDataCollection-maskedModeDisplay',
  subjectId: 'patientDataCollection-subjectId',
  mrn: 'patientDataCollection-mrn',
  firstName: 'patientDataCollection-firstName',
  lastName: 'patientDataCollection-lastName',
  genderSelector: 'patientDataCollection-genderSelector',
  genderOptions: {
    none: 'patientDataCollection-genderSelector-values-none',
    male: 'patientDataCollection-genderSelector-values-male',
    female: 'patientDataCollection-genderSelector-values-female',
    other: 'patientDataCollection-genderSelector-values-other',
    unkown: 'patientDataCollection-genderSelector-values-unknown',
  },
  genderIdentitySelector: 'patientDataCollection-genderIdentitySelector',
  genderIdentityOptions: {
    none: 'patientDataCollection-genderIdentitySelector-values-none',
    male: 'patientDataCollection-genderIdentitySelector-values-male',
    female: 'patientDataCollection-genderIdentitySelector-values-female',
    nonBinary: 'patientDataCollection-genderIdentitySelector-values-nonBinary',
    transgenderMale:
      'patientDataCollection-genderIdentitySelector-values-transgenderMale',
    transgenderFemale:
      'patientDataCollection-genderIdentitySelector-values-transgenderFemale',
    other: 'patientDataCollection-genderIdentitySelector-values-other',
    nonDisclose:
      'patientDataCollection-genderIdentitySelector-values-nonDisclose',
  },
  raceSelector: 'patientDataCollection-raceSelector',
  getRaceOption: (value: RaceCodes | '') =>
    `patientDataCollection-raceSelector-values-${value || 'NONE'}`,
  address: 'patientDataCollection-address',
  city: 'patientDataCollection-city',
  state: 'patientDataCollection-state',
  email: 'patientDataCollection-email',
  phoneNumber: 'patientDataCollection-phoneNumber',
  dob: 'patientDataCollection-dob',
  dod: 'patientDataCollection-dod',
  socialSecurityNumber: 'patientDataCollection-socialSecurityNumber',
  postalCode: 'patientDataCollection-postalCode',
  cancelSubbmissionButton: 'patientDataCollection-cancelSubmit',
  submissionButton: 'patientDataCollection-submit',
};

const messages = defineMessages({
  maskedMode: {
    id: 'patientDataColleciton.disabled.maskedMode',
    defaultMessage:
      'You cannot be in masked mode to view or modify patient data',
  },
  subjectId: {
    id: 'patientDataCollection.labels.subjectId',
    defaultMessage: 'Subject Id',
  },
  mrn: {
    id: 'patientDataCollection.labels.mrn',
    defaultMessage: 'Medical Record Number (MRN)',
  },
  firstName: {
    id: 'patientDataColleciton.labels.firstName',
    defaultMessage: 'First Name',
  },
  lastName: {
    id: 'patientDataColleciton.labels.lastName',
    defaultMessage: 'Last Name',
  },
  address: {
    id: 'patientDataColleciton.labels.address',
    defaultMessage: 'Address',
  },
  city: {
    id: 'patientDataColleciton.labels.city',
    defaultMessage: 'City',
  },
  state: {
    id: 'patientDataColleciton.labels.state',
    defaultMessage: 'State/Region/Province',
  },
  country: {
    id: 'patientDataColleciton.labels.country',
    defaultMessage: 'Country',
  },
  email: {
    id: 'patientDataColleciton.labels.email',
    defaultMessage: 'Email',
  },
  emailHelp: {
    id: 'patientDataCollection.email.emailHelp',
    defaultMessage: 'Email is required to invite a subject user',
  },
  subjectExists: {
    id: 'patientDataColleciton.email.subjectExists',
    defaultMessage: 'A subject with this email already exists',
  },
  phoneNumber: {
    id: 'patientDataColleciton.labels.phoneNumber',
    defaultMessage: 'Phone Number',
  },
  socialSecurityNumber: {
    id: 'patientDataCollection.labels.socialSecurityNumber',
    defaultMessage: 'Social Security Number',
  },
  postalCode: {
    id: 'patientDataColleciton.postalCode',
    defaultMessage: 'Zip Code',
  },
  dob: {
    id: 'patientDataColleciton.labels.dob',
    defaultMessage: 'Date of Birth',
  },
  dod: {
    id: 'patientDataColleciton.labels.dod',
    defaultMessage: 'Date of Death',
  },
  cancel: {
    id: 'patientDataColleciton.button.cancel',
    defaultMessage: 'Cancel',
  },
  save: {
    id: 'patientDataColleciton.button.save',
    defaultMessage: 'Save',
  },
  male: {
    id: 'patientDataColleciton.male',
    defaultMessage: 'Male',
  },
  female: {
    id: 'patientDataColleciton.female',
    defaultMessage: 'Female',
  },
  other: {
    id: 'patientDataColleciton.other',
    defaultMessage: 'Other',
  },
  unknown: {
    id: 'patientDataColleciton.unknown',
    defaultMessage: 'Unknown',
  },
  gender: {
    id: 'patientDataColleciton.gender',
    defaultMessage: 'Biological Sex',
  },
  genderIdentity: {
    id: 'patientDataColleciton.genderIdentity',
    defaultMessage: 'Gender Identity',
  },
  'non-binary': {
    id: 'patientDataColleciton.non-binary',
    defaultMessage: 'Non-Binary',
  },
  'transgender-male': {
    id: 'patientDataColleciton.transgender-male',
    defaultMessage: 'Transgender Male',
  },
  'transgender-female': {
    id: 'patientDataColleciton.transgender-female',
    defaultMessage: 'Transgender Female',
  },
  'non-disclose': {
    id: 'patientDataColleciton.non-disclose',
    defaultMessage: 'Do not wish to disclose',
  },
  none: {
    id: 'patientDataColleciton.none',
    defaultMessage: 'No Value',
  },
  '1002-5': {
    id: 'patientDataCollection.race.code.1002-5',
    defaultMessage: 'American Indian or Alaska Native',
  },
  '2028-9': {
    id: 'patientDataCollection.race.code.2028-9',
    defaultMessage: 'Asian',
  },
  '2054-5': {
    id: 'patientDataCollection.race.code.2054-5',
    defaultMessage: 'Black or African American',
  },
  '2076-8': {
    id: 'patientDataCollection.race.code.2076-8',
    defaultMessage: 'Native Hawaiian or Other Pacific Islander',
  },
  '2106-3': {
    id: 'patientDataCollection.race.code.2106-3',
    defaultMessage: 'White',
  },
  '2131-1': {
    id: 'patientDataCollection.race.code.2131-1',
    defaultMessage: 'Other Race',
  },
  race: {
    id: 'patientDataCollection.race.label',
    defaultMessage: 'Race',
  },
  required: {
    id: 'patientDataCollection.required',
    defaultMessage: 'Required',
  },
});

const styles = (theme: Theme) =>
  createStyles({
    root: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
    },
    form: {
      width: '100%',
      maxWidth: 500,
    },
    submitContainer: {
      display: 'flex',
      marginTop: theme.spacing(4),
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    cancelButton: {
      marginRight: theme.spacing(2),
    },
    updatingContainer: {
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },
    selectFormControl: {
      width: '100%',
    },
  });

export type Fields =
  | 'subjectId'
  | 'mrn'
  | 'firstName'
  | 'lastName'
  | 'address'
  | 'city'
  | 'postalCode'
  | 'state'
  | 'country'
  | 'email'
  | 'phoneNumber'
  | 'dob'
  | 'dod'
  | 'socialSecurityNumber'
  | 'gender'
  | 'genderIdentity'
  | 'race';

export function getAllFields(
  formatMessage: FormatMessage,
): { id: Fields; label: string }[] {
  const fields: Fields[] = [
    'subjectId',
    'mrn',
    'firstName',
    'lastName',
    'gender',
    'genderIdentity',
    'race',
    'address',
    'city',
    'state',
    'country',
    'postalCode',
    'email',
    'phoneNumber',
    'dob',
    'dod',
    'socialSecurityNumber',
  ];

  return fields.map(field => {
    return {
      id: field,
      label: formatMessage(messages[field]),
    };
  });
}

export interface PatientDataCollectionStateProps {
  maskedModeState: MaskedModeState;
}

export interface PatientDataCollectionOwnProps {
  patient: fhir.Patient;
  mrnConfig?: any;
  className?: string;
  onPatientUpdate: (patient: fhir.Patient) => void;
  textFieldProps: TextFieldProps;
  disableUpdates?: boolean;
  overrideMaskedMode?: boolean;
  isUpdating?: boolean;
  requiredFields?: Fields[];
  hideFields?: Fields[];
  intlPhoneNumberFormat?: SupportedIntlPhoneNumberValidation;
  onCancel?: () => void;
  patientAlreadyExists?: boolean;
  showAdminNotes?: boolean;
}

export interface PatientDataCollectionState {
  initialFormValues: PatientDataFormValue;
  initialPatient: fhir.Patient;
}

export type PatientDataCollectionProps = PatientDataCollectionOwnProps &
  PatientDataCollectionStateProps &
  WithStyles<typeof styles> &
  WrappedComponentProps;

class PatientDataCollection extends PureComponent<
  PatientDataCollectionProps,
  PatientDataCollectionState
> {
  private validators: Validators;

  constructor(props: PatientDataCollectionProps) {
    super(props);

    this.validators = getValidators(this.props.intl.formatMessage);
    this.state = {
      initialFormValues: patientToFormValue(props.patient, props.mrnConfig),
      initialPatient: props.patient,
    };
  }

  handleSubmit = (formValue: PatientDataFormValue) => {
    const updatedPatient = formValueToPatient(
      formValue,
      this.state.initialPatient,
      this.props.mrnConfig,
    );
    this.props.onPatientUpdate(updatedPatient);

    this.setState({
      initialFormValues: formValue,
      initialPatient: updatedPatient,
    });
  };

  getResetHandler = (formikReset: () => void) => () => {
    formikReset();

    this.props.onCancel?.();
  };

  render() {
    const {
      intl: { formatMessage },
      maskedModeState,
      classes,
      className,
      textFieldProps = {} as TextFieldProps,
      disableUpdates,
      overrideMaskedMode,
      isUpdating,
      requiredFields,
      patientAlreadyExists,
      hideFields,
      intlPhoneNumberFormat,
      showAdminNotes = false,
    } = this.props;

    if (isUpdating) {
      return (
        <div className={classes.updatingContainer}>
          <DotLoader />
        </div>
      );
    }

    const isFieldRequired = (field: Fields) =>
      (requiredFields || []).includes(field);

    const getValidator = (
      field: Fields,
      ...otherValidators: ValidationSignature[]
    ) => {
      const requiredValidator = isFieldRequired(field)
        ? this.validators.required
        : null;

      // @ts-expect-error This directive was added as part of a bulk-change to enable strictNullChecks.
      return combineValidators(requiredValidator, ...otherValidators);
    };

    const phoneNumberValidator = getValidator(
      'phoneNumber',
      this.validators.phoneNumber,
    );

    const intlPhoneNumberValidator = getValidator(
      'phoneNumber',
      this.validators.intlPhoneNumber,
    );

    const dobValidator = getValidator(
      'dob',
      this.validators.date(formDateFormats.date),
    );

    const dodValidator = getValidator(
      'dod',
      this.validators.date(formDateFormats.date),
    );

    const socialSecurityNumberValidator = getValidator(
      'socialSecurityNumber',
      this.validators.socialSecurityNumber,
    );

    const assertNotHidden = (field: Fields) =>
      !(hideFields || []).includes(field);

    const getSecondaryLabel = (field: Fields) =>
      isFieldRequired(field) ? formatMessage(messages.required) : null;

    return (
      <div className={clsx(classes.root, className)}>
        {maskedModeState.active && !overrideMaskedMode ? (
          <Text {...getTestProps(testIds.maskedModeDisplay)} size="headline">
            {formatMessage(messages.maskedMode)}
          </Text>
        ) : (
          <Formik
            enableReinitialize
            initialValues={this.state.initialFormValues}
            onSubmit={this.handleSubmit}
            render={formikProps => {
              return (
                <Form
                  className={classes.form}
                  {...getTestProps(testIds.rootFormElement)}
                >
                  <FormBox>
                    <PatientCohortAssignmentAlert
                      isCreatingPatient={!this.state.initialPatient}
                    />
                    {assertNotHidden('subjectId') && (
                      <Field
                        name="subjectId"
                        validate={getValidator('subjectId')}
                        component={FormikTextField}
                        label={formatMessage(messages.subjectId)}
                        disabled={disableUpdates}
                        secondaryLabel={getSecondaryLabel('subjectId')}
                        {...getTestProps(testIds.subjectId)}
                        {...textFieldProps}
                      />
                    )}
                    {assertNotHidden('mrn') && (
                      <>
                        {(!Array.isArray(formikProps.values.mrn)
                          ? [formikProps.values.mrn]
                          : formikProps.values.mrn
                        ).map((_ignore: string, index: number) => (
                          <Field
                            key={`mrn-${index}`}
                            name={`mrn[${index}]`}
                            validate={getValidator('mrn')}
                            component={FormikTextField}
                            label={formatMessage(messages.mrn)}
                            disabled={disableUpdates}
                            secondaryLabel={getSecondaryLabel('mrn')}
                            {...getTestProps(`${testIds.mrn}-${index}`)}
                            {...textFieldProps}
                          />
                        ))}
                      </>
                    )}
                    {assertNotHidden('firstName') && (
                      <Field
                        name="name.firstName"
                        validate={getValidator('firstName')}
                        component={FormikTextField}
                        label={formatMessage(messages.firstName)}
                        secondaryLabel={getSecondaryLabel('firstName')}
                        disabled={disableUpdates}
                        {...getTestProps(testIds.firstName)}
                        {...textFieldProps}
                      />
                    )}
                    {assertNotHidden('lastName') && (
                      <Field
                        name="name.lastName"
                        validate={getValidator('lastName')}
                        component={FormikTextField}
                        label={formatMessage(messages.lastName)}
                        secondaryLabel={getSecondaryLabel('lastName')}
                        disabled={disableUpdates}
                        {...getTestProps(testIds.lastName)}
                        {...textFieldProps}
                      />
                    )}
                    {assertNotHidden('gender') && (
                      <Field
                        id="genderSelect"
                        name="gender"
                        label={formatMessage(messages.gender)}
                        disabled={disableUpdates}
                        validate={getValidator('gender')}
                        component={FormikSelect}
                        value={formikProps.values.gender}
                        fullWidth
                        secondaryLabel={getSecondaryLabel('gender')}
                        {...getTestProps(testIds.genderSelector)}
                      >
                        {!isFieldRequired('gender') && (
                          <SelectOption
                            value=""
                            {...getTestProps(testIds.genderOptions.none)}
                          >
                            <em>{formatMessage(messages.none)}</em>
                          </SelectOption>
                        )}
                        <SelectOption
                          value="male"
                          title={formatMessage(messages.male)}
                          {...getTestProps(testIds.genderOptions.male)}
                        />
                        <SelectOption
                          value="female"
                          title={formatMessage(messages.female)}
                          {...getTestProps(testIds.genderOptions.female)}
                        />
                        <SelectOption
                          value="other"
                          title={formatMessage(messages.other)}
                          {...getTestProps(testIds.genderOptions.other)}
                        />
                        <SelectOption
                          value="unknown"
                          title={formatMessage(messages.unknown)}
                          {...getTestProps(testIds.genderOptions.unkown)}
                        />
                      </Field>
                    )}
                    {assertNotHidden('genderIdentity') && (
                      <Field
                        id="genderIdentitySelect"
                        name="genderIdentity"
                        label={formatMessage(messages.genderIdentity)}
                        disabled={disableUpdates}
                        validate={getValidator('genderIdentity')}
                        component={FormikSelect}
                        value={formikProps.values.genderIdentity}
                        fullWidth
                        secondaryLabel={getSecondaryLabel('genderIdentity')}
                        {...getTestProps(testIds.genderIdentitySelector)}
                      >
                        {!isFieldRequired('genderIdentity') && (
                          <SelectOption
                            value=""
                            {...getTestProps(
                              testIds.genderIdentityOptions.none,
                            )}
                          >
                            <em>{formatMessage(messages.none)}</em>
                          </SelectOption>
                        )}
                        <SelectOption
                          value="male"
                          title={formatMessage(messages.male)}
                          {...getTestProps(testIds.genderIdentityOptions.male)}
                        />
                        <SelectOption
                          value="female"
                          title={formatMessage(messages.female)}
                          {...getTestProps(
                            testIds.genderIdentityOptions.female,
                          )}
                        />
                        <SelectOption
                          value="non-binary"
                          title={formatMessage(messages['non-binary'])}
                          {...getTestProps(
                            testIds.genderIdentityOptions.nonBinary,
                          )}
                        />
                        <SelectOption
                          value="transgender-male"
                          title={formatMessage(messages['transgender-male'])}
                          {...getTestProps(
                            testIds.genderIdentityOptions.transgenderMale,
                          )}
                        />
                        <SelectOption
                          value="transgender-female"
                          title={formatMessage(messages['transgender-female'])}
                          {...getTestProps(
                            testIds.genderIdentityOptions.transgenderFemale,
                          )}
                        />
                        <SelectOption
                          value="other"
                          title={formatMessage(messages.other)}
                          {...getTestProps(testIds.genderIdentityOptions.other)}
                        />
                        <SelectOption
                          value="non-disclose"
                          title={formatMessage(messages['non-disclose'])}
                          {...getTestProps(
                            testIds.genderIdentityOptions.nonDisclose,
                          )}
                        />
                      </Field>
                    )}
                    {assertNotHidden('race') && (
                      <Field
                        id="raceSelect"
                        name="race"
                        label={formatMessage(messages.race)}
                        disabled={disableUpdates}
                        validate={getValidator('race')}
                        component={FormikSelect}
                        value={formikProps.values.race}
                        fullWidth
                        secondaryLabel={getSecondaryLabel('race')}
                        {...getTestProps(testIds.raceSelector)}
                      >
                        {!isFieldRequired('race') && (
                          <SelectOption
                            value=""
                            {...getTestProps(testIds.getRaceOption(''))}
                          >
                            <em>{formatMessage(messages.none)}</em>
                          </SelectOption>
                        )}
                        {allRaceCodes.map(_ => (
                          <SelectOption
                            key={_}
                            value={_}
                            title={formatMessage(messages[_])}
                            {...getTestProps(testIds.getRaceOption(_))}
                          />
                        ))}
                      </Field>
                    )}
                    {assertNotHidden('address') && (
                      <Field
                        name="address.text"
                        component={FormikTextField}
                        validate={getValidator('address')}
                        label={formatMessage(messages.address)}
                        disabled={disableUpdates}
                        secondaryLabel={getSecondaryLabel('address')}
                        {...getTestProps(testIds.address)}
                        {...textFieldProps}
                      />
                    )}
                    {assertNotHidden('city') && (
                      <Field
                        name="address.city"
                        component={FormikTextField}
                        validate={getValidator('city')}
                        label={formatMessage(messages.city)}
                        disabled={disableUpdates}
                        secondaryLabel={getSecondaryLabel('city')}
                        {...getTestProps(testIds.city)}
                        {...textFieldProps}
                      />
                    )}
                    {assertNotHidden('postalCode') && (
                      <Field
                        name="address.postalCode"
                        component={FormikTextField}
                        validate={getValidator(
                          'postalCode',
                          this.validators.postalCode,
                        )}
                        label={formatMessage(messages.postalCode)}
                        disabled={disableUpdates}
                        secondaryLabel={getSecondaryLabel('postalCode')}
                        {...getTestProps(testIds.postalCode)}
                        {...textFieldProps}
                      />
                    )}
                    {assertNotHidden('state') && (
                      <Field
                        name="address.state"
                        component={FormikTextField}
                        validate={getValidator('state')}
                        label={formatMessage(messages.state)}
                        disabled={disableUpdates}
                        secondaryLabel={getSecondaryLabel('state')}
                        {...getTestProps(testIds.state)}
                        {...textFieldProps}
                      />
                    )}
                    {assertNotHidden('country') && (
                      <Field
                        name="address.country"
                        validate={getValidator('country')}
                        component={FormikSelect}
                        label={formatMessage(messages.country)}
                        disabled={disableUpdates}
                        value={formikProps.values.address.country}
                        fullWidth
                      >
                        {getCountriesWithCodes().map(country => (
                          <SelectOption
                            key={country.alpha3Code}
                            value={country.alpha3Code}
                            title={country.name}
                          />
                        ))}
                      </Field>
                    )}
                    {assertNotHidden('email') && (
                      <Field
                        hasError={patientAlreadyExists}
                        errorMessage={formatMessage(messages.subjectExists)}
                        name="email"
                        validate={getValidator('email', this.validators.email)}
                        component={FormikTextField}
                        label={formatMessage(messages.email)}
                        helpMessage={
                          showAdminNotes
                            ? formatMessage(messages.emailHelp)
                            : null
                        }
                        disabled={disableUpdates}
                        secondaryLabel={getSecondaryLabel('email')}
                        {...getTestProps(testIds.email)}
                        {...textFieldProps}
                      />
                    )}
                    {assertNotHidden('phoneNumber') && (
                      <Field
                        name="phoneNumber"
                        validate={
                          intlPhoneNumberFormat === 'none'
                            ? intlPhoneNumberValidator
                            : phoneNumberValidator
                        }
                        validateInput={
                          intlPhoneNumberFormat === 'none'
                            ? intlPhoneNumberValidator
                            : phoneNumberValidator
                        }
                        component={PhoneNumberField}
                        label={formatMessage(messages.phoneNumber)}
                        disabled={disableUpdates}
                        intlFormat={intlPhoneNumberFormat}
                        secondaryLabel={getSecondaryLabel('phoneNumber')}
                        {...getTestProps(testIds.phoneNumber)}
                        input={{
                          ...textFieldProps,
                          type: 'tel',
                          disabled: disableUpdates,
                        }}
                      />
                    )}
                    {assertNotHidden('dob') && (
                      <Field
                        name="dob"
                        validate={dobValidator}
                        component={DateField}
                        label={formatMessage(messages.dob)}
                        disabled={disableUpdates}
                        secondaryLabel={getSecondaryLabel('dob')}
                        {...getTestProps(testIds.dob)}
                        input={{
                          ...textFieldProps,
                          disabled: disableUpdates,
                        }}
                      />
                    )}
                    {assertNotHidden('dod') && (
                      <Field
                        name="dod"
                        validate={dodValidator}
                        component={DateField}
                        label={formatMessage(messages.dod)}
                        disabled={disableUpdates}
                        secondaryLabel={getSecondaryLabel('dod')}
                        {...getTestProps(testIds.dod)}
                        input={{
                          ...textFieldProps,
                          disabled: disableUpdates,
                        }}
                      />
                    )}
                    {assertNotHidden('socialSecurityNumber') && (
                      <Field
                        name="socialSecurityNumber"
                        validate={socialSecurityNumberValidator}
                        validateInput={socialSecurityNumberValidator}
                        component={SocialSecurityNumberField}
                        label={formatMessage(messages.socialSecurityNumber)}
                        secondaryLabel={getSecondaryLabel(
                          'socialSecurityNumber',
                        )}
                        {...getTestProps(testIds.socialSecurityNumber)}
                        input={{
                          ...textFieldProps,
                          disabled: disableUpdates,
                        }}
                      />
                    )}
                    {!disableUpdates && (
                      <div className={classes.submitContainer}>
                        <Button
                          onClick={this.getResetHandler(
                            formikProps.handleReset,
                          )}
                          className={classes.cancelButton}
                          variant="text"
                          {...getTestProps(testIds.cancelSubbmissionButton)}
                        >
                          {formatMessage(messages.cancel)}
                        </Button>
                        <Button
                          disabled={!formikProps.dirty}
                          type="submit"
                          variant="contained"
                          {...getTestProps(testIds.submissionButton)}
                        >
                          {formatMessage(messages.save)}
                        </Button>
                      </div>
                    )}
                  </FormBox>
                </Form>
              );
            }}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = (
  state: RootState,
): PatientDataCollectionStateProps => ({
  maskedModeState: maskedModeSelectors.selectMaskedModeState(state),
});

export default withStyles(styles)(
  connect(mapStateToProps)(injectIntl(PatientDataCollection)),
);
